import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, GeoJSON } from 'react-leaflet';
import { FiMapPin, FiSearch, FiLoader } from 'react-icons/fi';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CARTO_DARK_URL } from "../../../lib/basemap";

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Location data returned from reverse geocoding
export interface LocationData {
  latitude: number;
  longitude: number;
  state?: string;
  county?: string;
  city?: string;
  zipCode?: string;
  timezone?: string;
  baCode?: string;
  nercRegion?: string;
}

interface LocationPickerProps {
  latitude: number | string;
  longitude: number | string;
  onChange: (data: LocationData) => void;
}

// Component to handle map click events
function MapClickHandler({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to recenter map when coordinates change externally
function MapRecenter({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();

  useEffect(() => {
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng], map.getZoom());
    }
  }, [lat, lng, map]);

  return null;
}

// US state abbreviation to timezone mapping (simplified - major timezones)
const STATE_TO_TIMEZONE: Record<string, string> = {
  'AL': 'America/Chicago', 'AK': 'America/Anchorage', 'AZ': 'America/Phoenix',
  'AR': 'America/Chicago', 'CA': 'America/Los_Angeles', 'CO': 'America/Denver',
  'CT': 'America/New_York', 'DE': 'America/New_York', 'FL': 'America/New_York',
  'GA': 'America/New_York', 'HI': 'Pacific/Honolulu', 'ID': 'America/Boise',
  'IL': 'America/Chicago', 'IN': 'America/Indiana/Indianapolis', 'IA': 'America/Chicago',
  'KS': 'America/Chicago', 'KY': 'America/New_York', 'LA': 'America/Chicago',
  'ME': 'America/New_York', 'MD': 'America/New_York', 'MA': 'America/New_York',
  'MI': 'America/Detroit', 'MN': 'America/Chicago', 'MS': 'America/Chicago',
  'MO': 'America/Chicago', 'MT': 'America/Denver', 'NE': 'America/Chicago',
  'NV': 'America/Los_Angeles', 'NH': 'America/New_York', 'NJ': 'America/New_York',
  'NM': 'America/Denver', 'NY': 'America/New_York', 'NC': 'America/New_York',
  'ND': 'America/Chicago', 'OH': 'America/New_York', 'OK': 'America/Chicago',
  'OR': 'America/Los_Angeles', 'PA': 'America/New_York', 'RI': 'America/New_York',
  'SC': 'America/New_York', 'SD': 'America/Chicago', 'TN': 'America/Chicago',
  'TX': 'America/Chicago', 'UT': 'America/Denver', 'VT': 'America/New_York',
  'VA': 'America/New_York', 'WA': 'America/Los_Angeles', 'WV': 'America/New_York',
  'WI': 'America/Chicago', 'WY': 'America/Denver', 'DC': 'America/New_York',
};

// EU/UK country code to timezone mapping
const COUNTRY_TO_TIMEZONE: Record<string, string> = {
  'GB': 'Europe/London', 'IE': 'Europe/Dublin',
  'DE': 'Europe/Berlin', 'FR': 'Europe/Paris', 'ES': 'Europe/Madrid',
  'IT': 'Europe/Rome', 'NL': 'Europe/Amsterdam', 'BE': 'Europe/Brussels',
  'AT': 'Europe/Vienna', 'CH': 'Europe/Zurich', 'PL': 'Europe/Warsaw',
  'CZ': 'Europe/Prague', 'SK': 'Europe/Bratislava', 'HU': 'Europe/Budapest',
  'RO': 'Europe/Bucharest', 'BG': 'Europe/Sofia', 'GR': 'Europe/Athens',
  'PT': 'Europe/Lisbon', 'DK': 'Europe/Copenhagen', 'SE': 'Europe/Stockholm',
  'NO': 'Europe/Oslo', 'FI': 'Europe/Helsinki', 'EE': 'Europe/Tallinn',
  'LV': 'Europe/Riga', 'LT': 'Europe/Vilnius', 'HR': 'Europe/Zagreb',
  'SI': 'Europe/Ljubljana', 'LU': 'Europe/Luxembourg',
};

// Detect if coordinates are in the US
function isUSLocation(lat: number, lng: number): boolean {
  return lng >= -170 && lng <= -60 && lat >= 18 && lat <= 72;
}

// Check if a point is inside a polygon (ray casting algorithm)
function pointInPolygon(point: [number, number], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

// Find which BA contains a point
function findBAForPoint(
  lat: number,
  lng: number,
  features: GeoJSON.Feature[]
): { baCode: string; feature: GeoJSON.Feature } | null {
  for (const feature of features) {
    const zoneName = feature.properties?.zoneName;
    // Only check US zones
    if (!zoneName?.startsWith('US-')) continue;

    const geometry = feature.geometry;
    if (geometry.type === 'Polygon') {
      for (const ring of (geometry as GeoJSON.Polygon).coordinates) {
        if (pointInPolygon([lng, lat], ring)) {
          return { baCode: zoneName, feature };
        }
      }
    } else if (geometry.type === 'MultiPolygon') {
      for (const polygon of (geometry as GeoJSON.MultiPolygon).coordinates) {
        for (const ring of polygon) {
          if (pointInPolygon([lng, lat], ring)) {
            return { baCode: zoneName, feature };
          }
        }
      }
    }
  }
  return null;
}

// Find which NERC region contains a point
function findNERCForPoint(
  lat: number,
  lng: number,
  features: GeoJSON.Feature[]
): string | null {
  for (const feature of features) {
    const nercCode = feature.properties?.NERC;
    if (!nercCode) continue;

    const geometry = feature.geometry;
    if (geometry.type === 'Polygon') {
      for (const ring of (geometry as GeoJSON.Polygon).coordinates) {
        if (pointInPolygon([lng, lat], ring)) {
          return nercCode;
        }
      }
    } else if (geometry.type === 'MultiPolygon') {
      for (const polygon of (geometry as GeoJSON.MultiPolygon).coordinates) {
        for (const ring of polygon) {
          if (pointInPolygon([lng, lat], ring)) {
            return nercCode;
          }
        }
      }
    }
  }
  return null;
}

export function LocationPicker({ latitude, longitude, onChange }: LocationPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [baBoundaries, setBaBoundaries] = useState<GeoJSON.FeatureCollection | null>(null);
  const [nercRegions, setNercRegions] = useState<GeoJSON.FeatureCollection | null>(null);
  const [detectedBA, setDetectedBA] = useState<string | null>(null);
  const [detectedNERC, setDetectedNERC] = useState<string | null>(null);
  const [selectedBAFeature, setSelectedBAFeature] = useState<GeoJSON.Feature | null>(null);

  // Parse coordinates
  const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
  const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;

  // Default to center of continental US if no coordinates
  const hasValidCoords = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  const mapCenter: [number, number] = hasValidCoords ? [lat, lng] : [39.8283, -98.5795];
  const initialZoom = hasValidCoords ? 8 : 4;

  // Load BA boundaries and NERC regions on mount
  useEffect(() => {
    // Load BA boundaries
    fetch('/data/ba_boundaries.geojson')
      .then(res => res.json())
      .then((data: GeoJSON.FeatureCollection) => {
        // Filter to only US zones for performance
        const usFeatures = data.features.filter(f =>
          f.properties?.zoneName?.startsWith('US-')
        );
        setBaBoundaries({ ...data, features: usFeatures });
      })
      .catch(err => console.error('Failed to load BA boundaries:', err));

    // Load NERC regions
    fetch('/data/nerc_regions.geojson')
      .then(res => res.json())
      .then((data: GeoJSON.FeatureCollection) => {
        setNercRegions(data);
      })
      .catch(err => console.error('Failed to load NERC regions:', err));
  }, []);

  // Reverse geocode to get location details
  const reverseGeocode = useCallback(async (lat: number, lng: number): Promise<Partial<LocationData>> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { 'User-Agent': 'TotalView-Platform/1.0' } }
      );
      const data = await response.json();

      if (data.address) {
        const addr = data.address;
        const countryCode = addr.country_code?.toUpperCase() || '';
        const isUS = countryCode === 'US';

        // State/region code from ISO format (e.g., "US-TX" -> "TX", "DE-BY" -> "BY")
        const isoCode = addr['ISO3166-2-lvl4'] || addr['ISO3166-2-lvl6'] || '';
        const stateCode = isoCode ? isoCode.split('-').pop() || '' : (addr.state || '');

        // City - try multiple fields in order of preference
        // For rural areas, use locality, place, or even suburb as fallback
        let city = addr.city || addr.town || addr.village || addr.municipality ||
                   addr.hamlet || addr.locality || addr.place || addr.suburb ||
                   addr.neighbourhood || '';

        // If still no city, try to extract from display_name (first meaningful part)
        if (!city && data.display_name) {
          const parts = data.display_name.split(',').map((p: string) => p.trim());
          // Skip the first part if it's just a road/address number, use the second
          if (parts.length > 1) {
            // Check if first part looks like an address (contains numbers)
            const firstPart = parts[0];
            if (/^\d/.test(firstPart) || /road|street|highway|avenue|drive|lane|way/i.test(firstPart)) {
              city = parts[1] || '';
            } else {
              city = firstPart;
            }
          }
        }

        // County - remove " County" suffix if present; for EU use county or state_district
        const county = (addr.county || addr.state_district || '').replace(/\s+County$/i, '');
        // Zip/postal code
        const zipCode = addr.postcode || '';

        // Determine timezone
        let timezone: string;
        if (isUS) {
          timezone = STATE_TO_TIMEZONE[stateCode] || 'America/Chicago';
        } else {
          timezone = COUNTRY_TO_TIMEZONE[countryCode] || 'UTC';
        }

        // For non-US, use state name or country for state field
        const stateDisplay = isUS ? stateCode : (addr.state || addr.country || countryCode);

        return {
          state: stateDisplay,
          county,
          city,
          zipCode,
          timezone,
        };
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
    }
    return {};
  }, []);

  // Handle location selection (click or search)
  const handleLocationSelect = useCallback(async (newLat: number, newLng: number) => {
    setIsReverseGeocoding(true);

    // Start with coordinates
    const locationData: LocationData = {
      latitude: newLat,
      longitude: newLng,
    };

    // BA/NERC detection only for US locations
    // For EU/UK, the backend API handles region detection automatically
    if (isUSLocation(newLat, newLng)) {
      // Find BA zone
      if (baBoundaries) {
        const baResult = findBAForPoint(newLat, newLng, baBoundaries.features);
        if (baResult) {
          // Extract just the BA code from zoneName (e.g., "US-NW-NEVP" -> "NEVP")
          // This matches the backend logic in ba_lookup.py
          const rawBaCode = baResult.baCode.split('-').pop() || baResult.baCode;
          locationData.baCode = rawBaCode;
          setDetectedBA(baResult.baCode); // Keep full name for display
          setSelectedBAFeature(baResult.feature);
        } else {
          setDetectedBA(null);
          setSelectedBAFeature(null);
        }
      }

      // Find NERC region
      if (nercRegions) {
        const rawNercCode = findNERCForPoint(newLat, newLng, nercRegions.features);
        if (rawNercCode && rawNercCode.trim() !== '-') {
          // Convert to ecoinvent-compatible format (e.g., "TRE" -> "US-TRE")
          // And remap SPP to US-MRO per backend logic in ba_lookup.py
          const formattedNerc = rawNercCode === 'SPP' ? 'US-MRO' : `US-${rawNercCode}`;
          locationData.nercRegion = formattedNerc;
          setDetectedNERC(formattedNerc);
        } else {
          setDetectedNERC(null);
        }
      }
    } else {
      // Non-US: clear BA/NERC (API will detect EU/UK region)
      setDetectedBA(null);
      setDetectedNERC(null);
      setSelectedBAFeature(null);
    }

    // Reverse geocode for address details
    const geoData = await reverseGeocode(newLat, newLng);
    Object.assign(locationData, geoData);

    setIsReverseGeocoding(false);
    onChange(locationData);
  }, [baBoundaries, nercRegions, onChange, reverseGeocode]);

  // Geocode search using Nominatim
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
        { headers: { 'User-Agent': 'TotalView-Platform/1.0' } }
      );
      const results = await response.json();

      if (results.length > 0) {
        const { lat, lon } = results[0];
        await handleLocationSelect(parseFloat(lat), parseFloat(lon));
        setSearchQuery('');
      } else {
        setSearchError('Location not found. Try a city name, address, or zip code.');
      }
    } catch (error) {
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Style for BA boundaries
  const baStyle = (feature: GeoJSON.Feature | undefined) => {
    const isSelected = feature?.properties?.zoneName === detectedBA;
    return {
      fillColor: isSelected ? '#009639' : '#1a365d',
      weight: isSelected ? 2 : 1,
      opacity: isSelected ? 1 : 0.5,
      color: isSelected ? '#009639' : '#4a5568',
      fillOpacity: isSelected ? 0.3 : 0.1,
    };
  };

  return (
    <section className="relative">
      <hr className="mb-8 border-t border-border" />
      <h4 className="mb-3 text-body font-semibold text-quantum-400 flex items-center gap-2">
        <FiMapPin className="text-lg" />
        Project Location
        <span className="text-health-400 ml-1">*</span>
      </h4>

      {/* Search bar */}
      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder="Search for a location (city, address, or postal code)..."
            className="input w-full pl-10"
          />
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        </div>
        <button
          type="button"
          onClick={() => handleSearch()}
          disabled={isSearching || !searchQuery.trim()}
          className="btn-secondary px-4 disabled:opacity-50"
        >
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {searchError && (
        <p className="mb-2 text-body-sm text-health-400">{searchError}</p>
      )}

      {/* Map container */}
      <div className="h-72 rounded-lg overflow-hidden border border-border relative">
        <MapContainer
          center={mapCenter}
          zoom={initialZoom}
          className="h-full w-full"
          style={{ background: '#0A1628' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url={CARTO_DARK_URL}
          />

          {/* BA Boundaries overlay */}
          {baBoundaries && (
            <GeoJSON
              key={detectedBA || 'ba-boundaries'}
              data={baBoundaries}
              style={baStyle}
              onEachFeature={(feature, layer) => {
                layer.bindTooltip(feature.properties?.zoneName || '', {
                  permanent: false,
                  direction: 'top',
                  className: 'bg-surface-700 text-text-primary border-border'
                });
              }}
            />
          )}

          <MapClickHandler onClick={handleLocationSelect} />
          <MapRecenter lat={lat} lng={lng} />
          {hasValidCoords && (
            <Marker position={[lat, lng]} icon={customIcon} />
          )}
        </MapContainer>

        {/* Loading overlay */}
        {isReverseGeocoding && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="flex items-center gap-2 bg-surface-700 px-4 py-2 rounded-lg">
              <FiLoader className="animate-spin text-quantum-400" />
              <span className="text-body-sm text-text-primary">Detecting location...</span>
            </div>
          </div>
        )}
      </div>

      {/* Detected Grid Regions */}
      {(detectedBA || detectedNERC) && (
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {detectedNERC && (
            <div className="flex items-center gap-2">
              <span className="text-body-sm text-text-secondary">NERC Region:</span>
              <span className="px-2 py-1 rounded bg-ecosystem-500/20 text-ecosystem-400 text-body-sm font-medium">
                {detectedNERC}
              </span>
            </div>
          )}
          {detectedBA && (
            <div className="flex items-center gap-2">
              <span className="text-body-sm text-text-secondary">Balancing Authority:</span>
              <span className="px-2 py-1 rounded bg-quantum-500/20 text-quantum-400 text-body-sm font-medium">
                {detectedBA}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Coordinates display */}
      <div className="mt-3 flex gap-4">
        <div className="flex-1">
          <label className="input-label">Latitude</label>
          <input
            type="number"
            step="any"
            value={hasValidCoords ? lat.toFixed(6) : ''}
            onChange={(e) => {
              const newLat = parseFloat(e.target.value) || 0;
              handleLocationSelect(newLat, lng || 0);
            }}
            placeholder="Click map or search"
            className="input w-full"
          />
        </div>
        <div className="flex-1">
          <label className="input-label">Longitude</label>
          <input
            type="number"
            step="any"
            value={hasValidCoords ? lng.toFixed(6) : ''}
            onChange={(e) => {
              const newLng = parseFloat(e.target.value) || 0;
              handleLocationSelect(lat || 0, newLng);
            }}
            placeholder="Click map or search"
            className="input w-full"
          />
        </div>
      </div>

      <p className="mt-2 text-caption text-text-muted">
        Click on the map to set location. US projects show BA boundaries; EU/UK grid regions are detected automatically by the API.
      </p>
    </section>
  );
}
